import { Controller } from "@hotwired/stimulus"
import { planningMutation } from "../planning_mutation"

// The employee layout's "Blocker" dialog (Planning::EmployeeBoard::BlockerDialogComponent). Opens
// on `planning:plan-blocker`:
//   - create: from the planning dialog's "Blocker" switch, or a type chip dropped on an aggregated
//             cell — employee and von..bis come from the row and the selection.
//   - edit:   from a click on a blocker bar (`blockerUrl` set) — type, dates and Bezeichnung of that
//             blocker, plus "Entfernen".
// The "Baustelle / Task" switch hands the same row and days back to the planning dialog.
export default class extends Controller {
  static targets = [
    "root",
    "title",
    "employee",
    "typeSelect",
    "from",
    "to",
    "label",
    "switchRow",
    "removeButton",
    "error",
    "submit",
    "spinner",
  ]
  static values = {
    createUrl: String,
    titleNew: String,
    titleEdit: String,
    removeTitle: String,
    removeBody: String,
  }

  connect() {
    this.current = null
    this.boundOpen = this.open.bind(this)
    this.boundKeydown = this.onKeydown.bind(this)
    document.addEventListener("planning:plan-blocker", this.boundOpen)
  }

  disconnect() {
    document.removeEventListener("planning:plan-blocker", this.boundOpen)
    document.removeEventListener("keydown", this.boundKeydown)
  }

  open(event) {
    const detail = event.detail || {}
    this.current = detail
    const editing = Boolean(detail.blockerUrl)

    this.titleTarget.textContent = editing ? this.titleEditValue : this.titleNewValue
    this.employeeTarget.textContent = detail.employeeName || "—"
    this.setType(detail.blockerTypeId, detail.blockerTypeName)
    this.setDate(this.fromTarget, detail.periodStart)
    this.setDate(this.toTarget, detail.periodEnd)
    this.labelTarget.value = detail.label || ""
    // hidden/flex swapped together: Tailwind emits .flex after .hidden, so both at once stays visible.
    this.switchRowTarget.classList.toggle("hidden", editing)
    this.switchRowTarget.classList.toggle("flex", !editing)
    this.removeButtonTarget.classList.toggle("hidden", !editing)
    this.errorTarget.classList.add("hidden")
    this.setPending(false)

    this.rootTarget.classList.remove("hidden")
    this.rootTarget.classList.add("flex")
    document.addEventListener("keydown", this.boundKeydown)
  }

  close() {
    this.rootTarget.classList.add("hidden")
    this.rootTarget.classList.remove("flex")
    this.current = null
    document.removeEventListener("keydown", this.boundKeydown)
  }

  async save() {
    if (!this.current) return
    const typeId = this.typeSelectTarget.value
    const from = this.fromTarget.value
    const to = this.toTarget.value
    if (!typeId || !from || !to) {
      this.errorTarget.classList.remove("hidden")
      return
    }

    const editing = Boolean(this.current.blockerUrl)
    this.setPending(true)
    const { ok } = await planningMutation({
      url: editing ? this.current.blockerUrl : this.createUrlValue,
      method: editing ? "PATCH" : "POST",
      body: {
        capacity_blocker: {
          employee_id: this.current.employeeId,
          capacity_blocker_type_id: Number(typeId),
          starts_on: from <= to ? from : to,
          ends_on: from <= to ? to : from,
          label: this.labelTarget.value,
        },
      },
      component: "planning-blocker-dialog",
    })
    // Kept open on a refusal (overlap, …) so the planner can adjust instead of starting over.
    if (ok) this.close()
    else this.setPending(false)
  }

  // Through the board's shared styled confirm, like a bar's ✕.
  remove() {
    if (!this.current?.blockerUrl) return
    const detail = {
      url: this.current.blockerUrl,
      title: this.removeTitleValue,
      body: this.removeBodyValue
        .replace("%{type}", this.selectedTypeName())
        .replace("%{name}", this.current.employeeName || ""),
    }
    this.close()
    document.dispatchEvent(new CustomEvent("planning:confirm-remove", { detail }))
  }

  // Same row and days, planned as a Baustelle/Aufgabe instead.
  switchToPlanning() {
    if (!this.current) return
    const { employeeId, employeeName } = this.current
    const detail = {
      mode: "plannablePick",
      employeeId,
      employeeName,
      resourceName: employeeName,
      periodStart: this.fromTarget.value || this.current.periodStart,
      periodEnd: this.toTarget.value || this.current.periodEnd,
    }
    this.close()
    document.dispatchEvent(new CustomEvent("planning:plan-range", { detail }))
  }

  // A create starts on the first type (most tenants keep one or two). A type deleted in
  // Einstellungen is gone from the rendered options; an edit still shows it, through an option
  // added for that edit only — left in place, the next create could pick a type the server refuses.
  setType(id, name) {
    const select = this.typeSelectTarget
    select.querySelectorAll("option[data-deleted-type]").forEach((option) => option.remove())
    const firstType = [...select.options].find((option) => option.value)?.value || ""
    const value = id ? String(id) : firstType
    if (value && ![...select.options].some((option) => option.value === value)) {
      const option = new Option(name || value, value)
      option.dataset.deletedType = "true"
      select.add(option)
    }
    select.value = value
    const controller = this.application.getControllerForElementAndIdentifier(select, "select")
    if (controller && typeof controller.refresh === "function") controller.refresh()
  }

  selectedTypeName() {
    const select = this.typeSelectTarget
    return select.options[select.selectedIndex]?.text || ""
  }

  // Common::DateFieldComponent mirrors its hidden ISO value into the visible field on `change`.
  setDate(target, iso) {
    target.value = iso || ""
    target.dispatchEvent(new Event("change", { bubbles: true }))
  }

  setPending(pending) {
    this.submitTarget.disabled = pending
    this.spinnerTarget.classList.toggle("hidden", !pending)
  }

  onKeydown(event) {
    if (event.key !== "Escape") return
    if (event.defaultPrevented || this.rootTarget.querySelector(":popover-open")) return

    this.close()
  }

  backdropPress(event) {
    this.pressedOnBackdrop = event.target === this.rootTarget
  }

  backdrop(event) {
    const startedOnBackdrop = this.pressedOnBackdrop
    this.pressedOnBackdrop = false
    if (event.target === this.rootTarget && startedOnBackdrop) this.close()
  }
}
